// results.js — pure assembly of palette sections/rows from the raw sources.
// WL (Fable Hail Mary Wave 5). Kept side-effect-free + unit-tested
// (results.test.js) so keyboard-navigation indexing can never silently drift
// from what is rendered.
//
//   assembleResults({ query, actions, segments, wheelItems, recents }) ->
//     { sections, flatRows, options }
//
//   sections : [{ key, title, rows:[{item, indices}] }]   (for rendering)
//   flatRows : header + option rows, in visual order (for virtualization)
//   options  : just the option rows, in order (for arrow-key navigation); each
//              carries its flatIndex so the active row can be scrolled into view.

import { scoreItem, fuzzyMatch } from "./fuzzy";

function scoreAndSort(query, items) {
  const scored = [];
  for (const item of items || []) {
    const m = scoreItem(query, item);
    if (m) scored.push({ item, indices: m.indices, _score: m.score });
  }
  scored.sort((a, b) => b._score - a._score);
  return scored.map(({ item, indices }) => ({ item, indices }));
}

export function assembleResults({ query, actions = [], segments = [], wheelItems = [], recents = [] }) {
  const q = (query || "").trim();
  const sections = [];

  if (q === "") {
    if (recents.length) {
      sections.push({
        key: "recent",
        title: "Recent",
        rows: recents.map((item) => ({ item, indices: [] })),
      });
    }
    sections.push({
      key: "actions",
      title: recents.length ? "Jump to" : "Start here",
      rows: actions.map((item) => ({ item, indices: [] })),
    });
  } else {
    // Wheels are already server-filtered by the search term — keep the server's
    // relevance order, but compute indices so matched letters still highlight.
    const wheelRows = (wheelItems || []).map((item) => ({
      item,
      indices: (fuzzyMatch(q, item.label) || {}).indices || [],
    }));
    if (wheelRows.length) sections.push({ key: "wheels", title: "Wheels", rows: wheelRows });

    const segRows = scoreAndSort(q, segments);
    if (segRows.length) sections.push({ key: "segments", title: "In this wheel", rows: segRows });

    const actRows = scoreAndSort(q, actions);
    if (actRows.length) sections.push({ key: "actions", title: "Actions", rows: actRows });
  }

  const flatRows = [];
  const options = [];
  for (const sec of sections) {
    flatRows.push({ type: "header", title: sec.title, key: `h:${sec.key}` });
    for (const row of sec.rows) {
      const flatIndex = flatRows.length;
      const optionIndex = options.length;
      const entry = { type: "option", item: row.item, indices: row.indices, flatIndex, optionIndex };
      flatRows.push(entry);
      options.push(entry);
    }
  }

  return { sections, flatRows, options };
}

// Stable, selector-safe DOM id for an option (item ids contain ':').
export function optionDomId(itemId) {
  return `cmdk-opt-${String(itemId).replace(/[^a-zA-Z0-9_-]/g, "-")}`;
}
